import type { Timestamp } from "@firebase/firestore-types";
import { z } from "zod";
import type { OpenBankingAccountType, ProAccountAccountType } from "../account/accountType.js";
import type { Locale } from "../models.js";

export const supervisorOrgsFullPath = "supervisorOrgs";
export const supervisorOrgFullPath = (supervisorOrgId: string): string =>
  `${supervisorOrgsFullPath}/${supervisorOrgId}`;

export const usersPathFromSupervisorOrg = "users";
export const supervisorOrgUsersFullPath = (supervisorOrgId: string): string =>
  `${supervisorOrgFullPath(supervisorOrgId)}/${usersPathFromSupervisorOrg}`;
export const supervisorOrgUserFullPath = (supervisorOrgId: string, uid: string): string =>
  `${supervisorOrgUsersFullPath(supervisorOrgId)}/${uid}`;

export const groupsPathFromSupervisorOrg = "groups";
export const supervisorOrgGroupsFullPath = (supervisorOrgId: string): string =>
  `${supervisorOrgFullPath(supervisorOrgId)}/${groupsPathFromSupervisorOrg}`;

export type SupervisorOrg = {
  name: string;
  lang: Locale;
  defaultOpenBankingProvider?: OpenBankingAccountType;
  defaultProAccountProvider?: ProAccountAccountType;
  avatarPadding?: boolean;
  createdAt: Timestamp | undefined;
  apiKeyHash?: string;
  apiKeyLastChars?: string;
  apiKeyCreatedAt?: Timestamp;
  proAccountPriceOptions: number[];
  allowTransferRequests?: boolean;
  useWebhook?: boolean;
  contractSignatureContact?: ContractSignatureContact;
  contractType?: "signatureApi" | null;
  subscriptionType?: "stripe" | null;
};

export const contractTemplatesPathFromSupervisorOrg = "contractTemplates";
export const contractTemplatesFullPath = (supervisorOrgId: string): string =>
  `${supervisorOrgFullPath(supervisorOrgId)}/${contractTemplatesPathFromSupervisorOrg}`;

export type ContractSignatureContact = {
  email: string;
  name: string;
};

export type ContractTemplate = {
  name: string;
  createdAt: Timestamp;
  /**
   * Optional contact override for this template. Falls back to supervisorOrg.contractSignatureContact.
   */
  contractSignatureContact?: ContractSignatureContact;
};

export const supervisorOrgUserRoleSchema = z.enum([
  "supervisor",
  "externalApprover",
  "reader",
  "nonApprovingSupervisor",
]);
export type SupervisorOrgUserRole = z.infer<typeof supervisorOrgUserRoleSchema>;

export type SupervisorOrgUser = {
  email: string;
  uid: string;
  role: SupervisorOrgUserRole;
  createdAt: Timestamp;
  lastSignInAt?: Timestamp;
};

export type SupervisorOrgGroup = {
  name: string;
  createdAt: Timestamp | undefined;
};
